import { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { FaPlay, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getEPGData } from '../services/epgService';

function debounce(func, wait) {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

const EPGList = forwardRef(({ onSelectProgram }, ref) => {
  const [epgData, setEpgData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [currentProgram, setCurrentProgram] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  const SCROLL_BUFFER = 25;

  const listRef = useRef(null);
  const timeoutRef = useRef(null);
  const scrollCheckTimeout = useRef(null);
  const currentProgramRef = useRef(null);
  const initialToastRef = useRef(true);

  const parsedPrograms = useMemo(() => 
    epgData.map((p, index, array) => {
      const start = new Date(p.start);
      const endOriginal = new Date(p.end);
      const nextProgram = array[index + 1];
      const nextStart = nextProgram ? new Date(nextProgram.start) : null;

      const endAdjusted = nextStart && nextStart.getTime() === endOriginal.getTime() + 60000
        ? endOriginal.getTime() + 59999
        : endOriginal.getTime();

      return {
        ...p,
        start: start.getTime(),
        endOriginal: endOriginal.getTime(),
        endAdjusted: endAdjusted,
      };
    }),
    [epgData]
  );

  const currentProgramIndex = useMemo(() => 
    parsedPrograms.findIndex(p => 
      p.start <= currentTime && currentTime <= p.endAdjusted
    ),
    [parsedPrograms, currentTime]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const current = parsedPrograms.find(p => 
      p.start <= currentTime && currentTime <= p.endAdjusted
    );
  
    const prev = currentProgramRef.current;
    const isDifferent = !prev || 
      current?.id !== prev.id ||
      current?.title !== prev.title ||
      current?.start !== prev.start ||
      current?.endAdjusted !== prev.endAdjusted;
  
    if (current && isDifferent) {
      setCurrentProgram(current);
      currentProgramRef.current = current;
    }
  }, [parsedPrograms, currentTime]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (currentProgram) {
      const toastId = `program-${currentProgram.id}`;
      
      if (!toast.isActive(toastId)) {
        if (initialToastRef.current) {
          toast(
            <div>
              <div style={{ color: '#cccc', fontSize: '0.88rem', marginBottom: 1 }}>
                Now airing:
              </div>
              <div className="program-title">
                <FaPlay className="me-2" size={12} />
                {currentProgram.title}
              </div>
              <div className="program-description">
                {currentProgram.description}
              </div>
            </div>,
            {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              progress: undefined,
              className: 'epg-entry text-success current-program',
              toastId: toastId
            }
          );
          initialToastRef.current = false;
        } else {
          const timeout = setTimeout(() => {
            toast(
              <div>
                <div style={{ color: '#cccc', fontSize: '0.88rem', marginBottom: 1 }}>
                  Now airing:
                </div>
                <div className="program-title">
                  <FaPlay className="me-2" size={12} />
                  {currentProgram.title}
                </div>
                <div className="program-description">
                  {currentProgram.description}
                </div>
              </div>,
              {
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                progress: undefined,
                className: 'epg-entry text-success current-program',
                toastId: toastId
              }
            );
          }, 40000);
          return () => clearTimeout(timeout);
        }
      }
    }
  }, [currentProgram]);

  useEffect(() => {
    if (parsedPrograms.length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ref.current?.scrollToCurrent('smooth');
        });
      });
    }
  }, [parsedPrograms, ref]);

  const scheduleMidnightRefresh = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const now = Date.now();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const timeUntilMidnight = midnight - now;
    
    timeoutRef.current = setTimeout(async () => {
      setLastUpdate(Date.now());
      scheduleMidnightRefresh();
    }, timeUntilMidnight);
  }, []);

  const checkScrollPosition = useCallback(() => {
    const list = listRef.current;
    if (!list || !parsedPrograms.length) return;
    
    if (currentProgramIndex === -1) {
      setIsScrolled(false);
      return;
    }
  
    const targetElement = currentProgramIndex > 0 
      ? list.children[currentProgramIndex]
      : list.firstChild;
  
    if (!targetElement) return;
  
    const containerTopPadding = parseInt(getComputedStyle(list).paddingTop, 10) || 0;
    const targetPosition = targetElement.offsetTop - containerTopPadding - 20;
    
    const maxScroll = list.scrollHeight - list.clientHeight;
    const finalPosition = Math.min(targetPosition, maxScroll);

    const currentScroll = Math.floor(list.scrollTop);
    const isAtPosition = Math.abs(currentScroll - finalPosition) <= SCROLL_BUFFER;
    
    setIsScrolled(prev => isAtPosition ? false : true);
  }, [parsedPrograms, currentProgramIndex]);

  useImperativeHandle(ref, () => ({
    scrollToCurrent: (behavior = 'smooth') => {
      const list = listRef.current;
      if (!list || !parsedPrograms.length) return;
  
      cancelAnimationFrame(scrollCheckTimeout.current);
      list.style.scrollBehavior = behavior;
  
      const execute = () => {
        if (currentProgramIndex === -1) return;
  
        const targetElement = currentProgramIndex > 0 
          ? list.children[currentProgramIndex]
          : list.firstChild;
  
        if (!targetElement) return;
  
        const containerTopPadding = parseInt(getComputedStyle(list).paddingTop, 10) || 0;
        const scrollPosition = targetElement.offsetTop - containerTopPadding - 20;
        
        list.scrollTo({
          top: Math.min(scrollPosition, list.scrollHeight - list.clientHeight),
          behavior
        });

        setIsScrolled(false);
  
        list.addEventListener('scrollend', handleScrollComplete);
        setTimeout(handleScrollComplete, 200);
      };
  
      requestAnimationFrame(() => {
        requestAnimationFrame(execute);
      });
    }
  }));

  useEffect(() => {
    scheduleMidnightRefresh();
    return () => clearTimeout(timeoutRef.current);
  }, [scheduleMidnightRefresh]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = new Date();
        const lastDate = new Date(lastUpdate);
        if (now.getDate() !== lastDate.getDate() || now.getMonth() !== lastDate.getMonth()) {
          setLastUpdate(Date.now());
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastUpdate]);

  useEffect(() => {
    let isMounted = true;
    const fetchEPG = async () => {
      try {
        setEpgData([]);
        setLoading(true);
        const programs = await getEPGData();
        if (isMounted && programs?.length) {
          setEpgData(programs);
        }
      } catch (error) {
        if (isMounted) {
          setError(error.message);
          setEpgData([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchEPG();
    return () => { isMounted = false; };
  }, [lastUpdate]);

  useEffect(() => {
    const resizeDebounce = debounce(() => {
      if (listRef.current) {
        listRef.current.style.scrollBehavior = 'smooth';
      }
      ref.current?.scrollToCurrent('smooth');
    }, 200);
  
    const observer = new ResizeObserver(resizeDebounce);
    if (listRef.current) observer.observe(listRef.current);
    
    return () => {
      observer.disconnect();
      resizeDebounce.cancel();
    };
  }, [ref]);

  const handleScroll = useCallback(() => {
    cancelAnimationFrame(scrollCheckTimeout.current);
    scrollCheckTimeout.current = requestAnimationFrame(checkScrollPosition);
  }, [checkScrollPosition]);

  const handleScrollComplete = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
  
    list.style.scrollBehavior = '';
    checkScrollPosition();
  }, [checkScrollPosition]);

  if (loading) return (
    <div className="epg-list custom-scrollbar p-4">
      <div className="epg-entry text-muted">
        <div className="program-title">
          Loading program guide...
        </div>
        <div className="program-description">
          Please wait while we fetch the latest program information.
        </div>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="epg-list custom-scrollbar p-4">
      <div className="epg-entry text-danger" style={{ borderColor: '#dc3545' }}>
        <div className="program-title">
          <FaExclamationTriangle className="me-2" size={12} />
          Program Guide Unavailable
        </div>
        <div className="program-description">
          {error}
        </div>
        <div className="program-time mt-2" style={{ color: '#ff9999' }}>
          Please check back later for program information.
        </div>
      </div>
    </div>
  );

  const now = currentTime;

  return (
    <div>
      <div className="epg-list custom-scrollbar" ref={listRef} onScroll={handleScroll}>
      {parsedPrograms.map((program, index) => {
        const isCurrent = program.start <= now && now <= program.endAdjusted;
        const isPast = now > program.endAdjusted;
        const progressPercentage = Math.min(
          100,
          ((now - program.start) / (program.endAdjusted - program.start)) * 100
        );

        return (
            <div
              key={index}
              className={`epg-entry ${isPast ? 'text-muted' : isCurrent ? 'text-success current-program' : 'text-warning'}`}
              tabIndex={0}
              style={{ cursor: 'default', position: 'relative' }}
            >
              <div className="program-title">
                {isCurrent && <FaPlay className="me-2" size={12} />}
                {program.title}
              </div>
              <div className="program-description">{program.description}</div>
              <div className="program-time">
                {new Date(program.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -{' '}
                {new Date(program.endOriginal).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              
              {isCurrent && (
                <div className="progress-bar-container">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isScrolled && (
        <button className="scroll-to-current" onClick={() => ref.current.scrollToCurrent()}>
          Scroll Back
        </button>
      )}
    </div>
  );
});

export default EPGList;